var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Paneset, Pane } from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';
import { useReviewQueueNewItems } from '../hooks/useReviewQueueNewItems';
import { useErrorReportSummary } from '../hooks/useErrorReports';
import { formatReviewCountBadge } from '../utils/reviewCountBadge';
import { CORPUS_QUALITY_CAPABILITY } from '../utils/corpusQualityAccess';
import { ERROR_REPORTS_CAPABILITY } from '../utils/errorReportsAccess';
var BASE_PATH = '/czna-authorities';
/** Same perm the rail's Inbox entry uses (CZNA-1446) — every user of the module has it. */
var INBOX_CAPABILITY = 'czna-authorities.records.collection.get';
var INBOX_PATH = "".concat(BASE_PATH, "/inbox");
var ERROR_REPORTS_PATH = "".concat(BASE_PATH, "/error-reports");
var TILES = [
    { path: "".concat(BASE_PATH, "/authorities"), icon: '📇', nameId: 'ui-czna-authorities.nav.authorities', hintId: 'ui-czna-authorities.dashboard.tile.authorities.hint' },
    { path: "".concat(BASE_PATH, "/headings"), icon: '🔤', nameId: 'ui-czna-authorities.nav.headings', hintId: 'ui-czna-authorities.dashboard.tile.headings.hint' },
    { path: "".concat(BASE_PATH, "/vocabularies"), icon: '📚', nameId: 'ui-czna-authorities.nav.vocabularies', hintId: 'ui-czna-authorities.dashboard.tile.vocabularies.hint' },
    { path: "".concat(BASE_PATH, "/corpus-quality"), icon: '📊', nameId: 'ui-czna-authorities.nav.corpusQuality', hintId: 'ui-czna-authorities.dashboard.tile.corpusQuality.hint', perm: CORPUS_QUALITY_CAPABILITY },
];
var tileStyle = {
    textAlign: 'left',
    background: '#fff',
    border: '1px solid #d4dbe3',
    borderRadius: '10px',
    padding: '18px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minHeight: '108px',
    position: 'relative',
    font: 'inherit',
};
var DashboardTile = function (_a) {
    var _b, _c;
    var icon = _a.icon, name = _a.name, hint = _a.hint, onClick = _a.onClick, dashed = _a.dashed, badge = _a.badge, notice = _a.notice, ariaLabel = _a.ariaLabel, dataAttr = _a.dataAttr, _d = _a.badgeDataAttr, badgeDataAttr = _d === void 0 ? 'data-test-dashboard-review-count' : _d;
    return (React.createElement("button", __assign({ type: "button", onClick: onClick, "aria-label": ariaLabel }, (dataAttr ? (_b = {}, _b[dataAttr] = true, _b) : {}), { style: __assign(__assign({}, tileStyle), { borderStyle: dashed ? 'dashed' : 'solid' }) }),
        typeof badge === 'number' && badge > 0 ? (React.createElement("span", (_c = {}, _c[badgeDataAttr] = true, _c.style = {
            position: 'absolute',
            top: '14px',
            right: '14px',
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffe8a1',
            borderRadius: '999px',
            padding: '1px 9px',
            fontSize: '12px',
            fontWeight: 700,
        }, _c), formatReviewCountBadge(badge))) : null,
        React.createElement("span", { style: { fontSize: '22px' }, "aria-hidden": true }, icon),
        React.createElement("span", { style: { fontWeight: 600, fontSize: '15px' } }, name),
        React.createElement("span", { style: { fontSize: '12.5px', color: '#5d6b7a' } }, hint),
        notice ? (React.createElement("span", { "data-test-dashboard-review-new": true, style: { fontSize: '12.5px', fontWeight: 600, color: '#1f6f3f' } },
            "\u25CF ",
            notice)) : null));
};
/**
 * CZNA-314 — module landing page (root `/czna-authorities`). Replaces the former
 * redirect to `/proposals`. Minimal by design (MVP): category tiles plus the Inbox tile
 * carrying the count of what waits on the user (CZNA-1446; it was "Ke kontrole" before).
 *
 * <p>Future (out of MVP, per NK): simple statistics and a role-based "what is
 * waiting for me to approve in my vocabulary" overview — the placeholder block
 * below marks that intent without blocking the navigation work.
 */
var Dashboard = function () {
    var history = useHistory();
    var intl = useIntl();
    var stripes = useStripes();
    var hasInbox = stripes.hasPerm(INBOX_CAPABILITY);
    var _a = useReviewQueueNewItems(hasInbox), count = _a.count, hasNew = _a.hasNew;
    // CZNA-1596 — the backend says whether this user handles public error reports: an NK
    // supervisor (count of their types) or the authority administrator (count of all).
    var errorReports = useErrorReportSummary();
    // The route itself opens only with the capability (routesConfig) and shows "Přístup odepřen"
    // without it (CZNA-1627); never lead to a section the user cannot open.
    var showErrorReports = errorReports.canHandle && stripes.hasPerm(ERROR_REPORTS_CAPABILITY);
    var go = function (path) { return history.push(path); };
    var inboxLabel = intl.formatMessage({ id: 'ui-czna-authorities.nav.inbox' });
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.nav.dashboard" }) },
            React.createElement("div", { style: { padding: '20px 22px', maxWidth: '600px' }, "data-test-dashboard": true },
                React.createElement("p", { style: { color: '#5d6b7a', fontSize: '13.5px', margin: '0 0 18px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.subtitle" })),
                React.createElement("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' } },
                    TILES.filter(function (t) { return !t.perm || stripes.hasPerm(t.perm); }).map(function (t) { return (React.createElement(DashboardTile, { key: t.path, icon: t.icon, name: React.createElement(FormattedMessage, { id: t.nameId }), hint: React.createElement(FormattedMessage, { id: t.hintId }), onClick: function () { return go(t.path); }, ariaLabel: intl.formatMessage({ id: t.nameId }), dataAttr: "data-test-dashboard-tile" })); }),
                    hasInbox ? (React.createElement(DashboardTile, { icon: "\uD83D\uDCE5", name: inboxLabel, hint: React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.tile.inbox.hint" }), onClick: function () { return go(INBOX_PATH); }, dashed: true, badge: count, notice: hasNew
                            ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.tile.review.new" })
                            : undefined, ariaLabel: [
                            count > 0
                                ? intl.formatMessage({ id: 'ui-czna-authorities.nav.inbox.aria' }, { count: count })
                                : inboxLabel,
                            hasNew
                                ? intl.formatMessage({ id: 'ui-czna-authorities.dashboard.tile.review.new' })
                                : null,
                        ].filter(Boolean).join(', '), dataAttr: "data-test-dashboard-review-tile" })) : null,
                    showErrorReports ? (React.createElement(DashboardTile, { icon: "\uD83D\uDCE3", name: React.createElement(FormattedMessage, { id: "ui-czna-authorities.nav.errorReports" }), hint: React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.tile.errorReports.hint" }), onClick: function () { return go(ERROR_REPORTS_PATH); }, badge: errorReports.newCount, ariaLabel: errorReports.newCount > 0
                            ? intl.formatMessage({ id: 'ui-czna-authorities.dashboard.tile.errorReports.aria' }, { count: errorReports.newCount })
                            : intl.formatMessage({ id: 'ui-czna-authorities.nav.errorReports' }), dataAttr: "data-test-dashboard-error-reports-tile", badgeDataAttr: "data-test-dashboard-error-reports-count" })) : null),
                React.createElement("div", { style: {
                        marginTop: '16px',
                        border: '1px dashed #d4dbe3',
                        borderRadius: '10px',
                        padding: '14px 16px',
                        background: '#f8fafc',
                    }, "data-test-dashboard-future": true },
                    React.createElement("h3", { style: { margin: '0 0 4px', fontSize: '13px', color: '#5d6b7a' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.future.title" })),
                    React.createElement("p", { style: { margin: 0, fontSize: '12.5px', color: '#8a97a6' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.dashboard.future.body" })))))));
};
export default Dashboard;
